import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { roleActionErrorToast } from '@/lib/user-feedback';

export interface FleetVehicle {
  id: string;
  plate: string;
  brand: string;
  model: string;
  year: number | null;
  color: string;
  transmission: 'mechanical' | 'automatic';
  notes: string;
  active: boolean;
}

interface ListResponse {
  data: FleetVehicle[];
  meta: { total: number; page: number; perPage: number; lastPage: number };
}

type StatusFilter = '' | 'available' | 'maintenance';
type TransmissionFilter = '' | FleetVehicle['transmission'];

const SEARCH_FIELD =
  'h-12 w-full rounded-xl border border-[#c6c5d4] bg-white py-3.5 pl-11 pr-4 text-base text-slate-800 shadow-sm outline-none transition-all placeholder:text-slate-400 focus:border-dash-primary focus:ring-2 focus:ring-dash-primary';

const SELECT_FIELD =
  'h-12 appearance-none rounded-xl border border-[#c6c5d4] bg-white pl-3.5 pr-9 text-sm font-medium text-slate-700 shadow-sm outline-none transition-colors focus:border-dash-primary focus:ring-2 focus:ring-dash-primary';

function transmissionLabel(value: FleetVehicle['transmission']): string {
  return value === 'automatic' ? 'Automático' : 'Mecánico';
}

export function PracticalExamVehiclesPage() {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.EVALUACIONES_CREAR);
  const canDelete = hasPermission(PERMISSIONS.EVALUACIONES_ELIMINAR);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('');
  const [transmission, setTransmission] = useState<TransmissionFilter>('');
  const [page, setPage] = useState(1);
  const [pendingDelete, setPendingDelete] = useState<FleetVehicle | null>(null);

  const searchTerm = useDebouncedValue(searchQuery, 300).trim();
  const hasFilters = Boolean(searchTerm || status || transmission);

  const { data, isLoading, error } = useQuery({
    queryKey: ['practical-exam-vehicles', searchTerm, status, transmission, page],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (status === 'available') params.set('active', 'true');
      if (status === 'maintenance') params.set('active', 'false');
      if (transmission) params.set('transmission', transmission);
      params.set('page', String(page));
      return api<ListResponse>(`/practical-exams/vehicles?${params.toString()}`);
    },
  });

  useEffect(() => {
    setPage(1);
  }, [searchTerm, status, transmission]);

  const remove = useMutation({
    mutationFn: (id: string) => api(`/practical-exams/vehicles/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      const removed = pendingDelete;
      setPendingDelete(null);
      if ((data?.data.length ?? 0) === 1 && page > 1) setPage((current) => current - 1);
      await queryClient.invalidateQueries({ queryKey: ['practical-exam-vehicles'] });
      toast({
        title: 'Vehículo eliminado',
        description: removed ? `Se eliminó el vehículo ${removed.plate}.` : undefined,
        variant: 'success',
      });
    },
    onError: (removeError) => toast(roleActionErrorToast(removeError, 'No se pudo eliminar el vehículo')),
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;
  const total = meta?.total ?? 0;
  const perPage = meta?.perPage ?? (rows.length || 1);
  const lastPage = meta?.lastPage ?? 1;
  const showingFrom = total === 0 ? 0 : (page - 1) * perPage + 1;
  const showingTo = total === 0 ? 0 : Math.min(page * perPage, total);

  const actionBtn =
    'inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors';

  function clearFilters() {
    setSearchQuery('');
    setStatus('');
    setTransmission('');
    setPage(1);
  }

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=plugins" label="Volver a plugins" />
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Vehículos</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Patente, marca, modelo y transmisión de los vehículos del examen práctico.
            </p>
          </div>
        </div>
        {canCreate ? (
          <Link
            to="/examen-practico/vehiculos/nuevo"
            className="inline-flex h-10 shrink-0 items-center gap-2 self-end rounded-lg bg-dash-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#11187b] sm:self-auto"
          >
            <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
              add
            </span>
            Agregar vehículo
          </Link>
        ) : null}
      </div>

      {error ? (
        <p className="mb-4 text-sm text-error-600" role="alert">
          {(error as Error).message}
        </p>
      ) : null}

      <div className="flex flex-col overflow-hidden rounded-xl border border-dash-outline-variant bg-white shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-dash-outline-variant bg-white p-4 sm:p-5">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3.5">
            <form
              role="search"
              className="flex min-w-[240px] flex-1 items-center gap-2.5"
              onSubmit={(event) => {
                event.preventDefault();
                setPage(1);
              }}
            >
              <div className="relative min-w-[240px] flex-1">
                <span
                  className="material-symbols-outlined pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[22px] text-slate-400"
                  aria-hidden="true"
                >
                  search
                </span>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Buscar por patente, modelo..."
                  aria-label="Buscar vehículo"
                  className={SEARCH_FIELD}
                />
              </div>
            </form>
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="relative">
                <select
                  aria-label="Filtrar por estado"
                  value={status}
                  onChange={(event) => setStatus(event.target.value as StatusFilter)}
                  className={SELECT_FIELD}
                >
                  <option value="">Estado: Todos</option>
                  <option value="available">Disponible</option>
                  <option value="maintenance">En mantenimiento</option>
                </select>
                <span
                  className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[20px] text-slate-400"
                  aria-hidden="true"
                >
                  expand_more
                </span>
              </div>
              <div className="relative">
                <select
                  aria-label="Filtrar por transmisión"
                  value={transmission}
                  onChange={(event) => setTransmission(event.target.value as TransmissionFilter)}
                  className={SELECT_FIELD}
                >
                  <option value="">Transmisión: Todas</option>
                  <option value="mechanical">Mecánico</option>
                  <option value="automatic">Automático</option>
                </select>
                <span
                  className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[20px] text-slate-400"
                  aria-hidden="true"
                >
                  expand_more
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            title="Restablecer filtros de búsqueda"
            onClick={clearFilters}
            className="ml-auto inline-flex h-12 shrink-0 items-center gap-2 rounded-xl border border-[#c6c5d4] bg-white px-4 text-sm font-medium text-slate-600 shadow-sm transition-colors hover:bg-[#eff4ff] hover:text-dash-primary"
          >
            <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
              filter_alt_off
            </span>
            Limpiar filtros
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[840px] border-collapse text-left">
              <thead>
                <tr className="border-b border-dash-outline-variant bg-[#f8f9ff]">
                  <th className="px-6 py-3.5 text-xs font-semibold uppercase tracking-wider text-[#5b5d72]" scope="col">
                    Patente
                  </th>
                  <th className="px-6 py-3.5 text-xs font-semibold uppercase tracking-wider text-[#5b5d72]" scope="col">
                    Vehículo
                  </th>
                  <th className="px-6 py-3.5 text-xs font-semibold uppercase tracking-wider text-[#5b5d72]" scope="col">
                    Año
                  </th>
                  <th className="px-6 py-3.5 text-xs font-semibold uppercase tracking-wider text-[#5b5d72]" scope="col">
                    Transmisión
                  </th>
                  <th className="px-6 py-3.5 text-xs font-semibold uppercase tracking-wider text-[#5b5d72]" scope="col">
                    Estado
                  </th>
                  <th className="px-6 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-[#5b5d72]" scope="col">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dash-outline-variant text-sm text-slate-700">
                {rows.length === 0 ? (
                  <tr>
                    <td className="px-6 py-12 text-center" colSpan={6}>
                      <div className="flex flex-col items-center justify-center gap-2">
                        <span className="material-symbols-outlined text-3xl text-slate-400" aria-hidden="true">
                          search_off
                        </span>
                        <p className="text-sm font-medium text-slate-600">
                          {hasFilters
                            ? 'No se encontraron vehículos que coincidan con la búsqueda.'
                            : 'No hay vehículos registrados.'}
                        </p>
                        {hasFilters ? (
                          <p className="text-xs text-slate-400">
                            Prueba ajustando los términos de búsqueda o borra el filtro para ver todos los registros.
                          </p>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-[#f8f9ff]">
                      <td className="whitespace-nowrap px-6 py-4">
                        <span className="inline-flex items-center rounded-md border border-blue-200/60 bg-[#eff4ff] px-2.5 py-1 font-mono text-xs font-bold tracking-wider text-dash-primary">
                          {row.plate}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex flex-col">
                          <span className="font-medium text-[#131b2e]">
                            {row.brand} {row.model}
                          </span>
                          {row.color ? <span className="text-xs text-[#5b5d72]">{row.color}</span> : null}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 font-medium text-slate-600">{row.year ?? '—'}</td>
                      <td className="whitespace-nowrap px-6 py-4">{transmissionLabel(row.transmission)}</td>
                      <td className="whitespace-nowrap px-6 py-4">
                        {row.active ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#dcfce7] px-2.5 py-1 text-xs font-medium text-[#15803d]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#15803d]" aria-hidden="true" />
                            Disponible
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-600" aria-hidden="true" />
                            En mantenimiento
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-right">
                        <div className="inline-flex items-center justify-end gap-1">
                          <Link
                            to={`/examen-practico/vehiculos/${row.id}`}
                            className={`${actionBtn} hover:bg-[#eff4ff] hover:text-dash-primary`}
                            title="Editar"
                            aria-label={`Editar ${row.plate}`}
                          >
                            <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                              edit
                            </span>
                          </Link>
                          {canDelete ? (
                            <button
                              type="button"
                              onClick={() => setPendingDelete(row)}
                              className={`${actionBtn} hover:bg-rose-50 hover:text-[#dc2626]`}
                              title="Eliminar"
                              aria-label={`Eliminar ${row.plate}`}
                            >
                              <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                                delete
                              </span>
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading ? (
          <div className="flex flex-col items-center justify-between gap-4 border-t border-dash-outline-variant bg-white px-6 py-4 sm:flex-row">
            <p className="text-xs text-slate-500">
              {total === 0 ? (
                <>
                  Mostrando <span className="font-semibold text-slate-700">0</span> de{' '}
                  <span className="font-semibold text-slate-700">0</span> resultados
                </>
              ) : (
                <>
                  Mostrando <span className="font-semibold text-slate-700">{showingFrom}</span> a{' '}
                  <span className="font-semibold text-slate-700">{showingTo}</span> de{' '}
                  <span className="font-semibold text-slate-700">{total}</span> resultados
                </>
              )}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                aria-label="Página anterior"
                disabled={page <= 1}
                onClick={() => setPage((current) => current - 1)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-dash-outline-variant text-slate-400 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
              >
                <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                  chevron_left
                </span>
              </button>
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-dash-primary text-xs font-semibold text-white shadow-sm"
                aria-current="page"
              >
                {page}
              </div>
              <button
                type="button"
                aria-label="Página siguiente"
                disabled={page >= lastPage}
                onClick={() => setPage((current) => current + 1)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-dash-outline-variant text-slate-400 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
              >
                <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                  chevron_right
                </span>
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <ConfirmModal
        open={Boolean(pendingDelete)}
        title="Eliminar vehículo"
        description={
          pendingDelete ? `Se borra el vehículo ${pendingDelete.plate}. Esta acción no se puede deshacer.` : ''
        }
        confirmLabel="Eliminar"
        loading={remove.isPending}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </div>
  );
}
